import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib'
import {
  Training,
  TrainingChapter,
  TrainingLesson,
  TrainingWithChapters,
  Enrollment,
  EnrollmentProgress,
  LessonProgress,
  TrainingStatus,
  TrainingType,
  LessonContentType,
} from '@/types/database'
import type { InstructorEnrollmentRow } from './types'

// ---- Fetch all published trainings (student view) ----
export function useTrainings() {
  return useQuery({
    queryKey: ['trainings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trainings')
        .select('*, profiles!trainings_instructor_id_fkey(first_name, last_name)')
        .eq('status', 'published')
        .is('deleted_at', null)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as (Training & { profiles: { first_name: string | null; last_name: string | null } | null })[]
    },
  })
}

// ---- Fetch instructor's own trainings ----
export function useInstructorTrainings(instructorId: string) {
  return useQuery({
    queryKey: ['trainings', 'instructor', instructorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trainings')
        .select('*')
        .eq('instructor_id', instructorId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as Training[]
    },
    enabled: !!instructorId,
  })
}

// ---- Fetch single training with chapters + lessons ----
export function useTraining(trainingId: string) {
  return useQuery({
    queryKey: ['training', trainingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trainings')
        .select(`
          *,
          training_chapters (
            *,
            training_lessons (*)
          ),
          training_certifications (
            certifications (*)
          ),
          training_partners (
            partners (*)
          )
        `)
        .eq('id', trainingId)
        .single()

      if (error) throw error

      const training = data as TrainingWithChapters & {
        training_certifications: Array<{ certifications: any }>
        training_partners: Array<{ partners: any }>
      }
      training.training_chapters = (training.training_chapters ?? [])
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((chapter) => ({
          ...chapter,
          training_lessons: [...(chapter.training_lessons ?? [])].sort(
            (a, b) => a.sort_order - b.sort_order
          ),
        }))

      return training
    },
    enabled: !!trainingId,
  })
}

// ---- Create a training ----
export function useCreateTraining() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      title: string
      description?: string | null
      type: TrainingType
      instructor_id: string
      organization_id?: string | null
      thumbnail_url?: string | null
      max_students?: number | null
      start_date?: string | null
      end_date?: string | null
      duration_hours?: number | null
      duration_weeks?: number | null
      delivery_mode?: string | null
      objectives?: string[] | null
      target_audience?: string | null
      prerequisites?: string | null
      price_mad?: number | null
      price_eur?: number | null
    }) => {
      const { data, error } = await supabase
        .from('trainings')
        .insert({ ...payload, status: 'draft' as TrainingStatus })
        .select()
        .single()

      if (error) throw error
      return data as Training
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['trainings'] })
      queryClient.invalidateQueries({
        queryKey: ['trainings', 'instructor', variables.instructor_id],
      })
    },
  })
}

// ---- Update a training ----
export function useUpdateTraining() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      id: string
      instructor_id: string
      title?: string
      description?: string | null
      type?: TrainingType
      status?: TrainingStatus
      submitted_for_review_at?: string | null
      thumbnail_url?: string | null
      max_students?: number | null
      start_date?: string | null
      end_date?: string | null
      duration_hours?: number | null
      duration_weeks?: number | null
      delivery_mode?: string | null
      objectives?: string[] | null
      target_audience?: string | null
      prerequisites?: string | null
      price_mad?: number | null
      price_eur?: number | null
    }) => {
      const { id, instructor_id, ...updates } = payload
      void instructor_id
      const { data, error } = await supabase
        .from('trainings')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Training
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['trainings'] })
      queryClient.invalidateQueries({
        queryKey: ['trainings', 'instructor', variables.instructor_id],
      })
      queryClient.invalidateQueries({ queryKey: ['training', variables.id] })
    },
  })
}

// ---- Soft-delete a training ----
export function useDeleteTraining() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, instructor_id }: { id: string; instructor_id: string }) => {
      const { error } = await supabase
        .from('trainings')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)

      if (error) throw error
      return { id, instructor_id }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['trainings'] })
      queryClient.invalidateQueries({
        queryKey: ['trainings', 'instructor', variables.instructor_id],
      })
      queryClient.invalidateQueries({ queryKey: ['training', variables.id] })
    },
  })
}

// ---- Enroll student in a training ----
export function useEnroll() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      training_id,
      student_id,
      organization_id,
    }: {
      training_id: string
      student_id: string
      organization_id?: string | null
    }) => {
      const { data, error } = await supabase
        .from('enrollments')
        .insert({ training_id, student_id, organization_id })
        .select()
        .single()

      if (error) throw error
      return data as Enrollment
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['enrollments', variables.student_id] })
      queryClient.invalidateQueries({ queryKey: ['training', variables.training_id] })
    },
  })
}

// ---- Fetch enrollment progress from view ----
export function useEnrollmentProgress(enrollmentId: string) {
  return useQuery({
    queryKey: ['enrollment-progress', enrollmentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('enrollment_progress_view')
        .select('*')
        .eq('enrollment_id', enrollmentId)
        .single()

      if (error) throw error
      return data as EnrollmentProgress
    },
    enabled: !!enrollmentId,
  })
}

// ---- Mark a lesson complete (optimistic update) ----
export function useMarkLessonComplete() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      lesson_id,
      student_id,
    }: {
      lesson_id: string
      student_id: string
      enrollment_id: string
      content_type?: LessonContentType
    }) => {
      const { data, error } = await supabase
        .from('lesson_progress')
        .upsert(
          {
            lesson_id,
            student_id,
            completed: true,
            completed_at: new Date().toISOString(),
          },
          { onConflict: 'student_id,lesson_id' }
        )
        .select()
        .single()

      if (error) throw error
      return data
    },
    onMutate: async ({ enrollment_id }) => {
      await queryClient.cancelQueries({ queryKey: ['enrollment-progress', enrollment_id] })

      const snapshot = queryClient.getQueryData<EnrollmentProgress>([
        'enrollment-progress',
        enrollment_id,
      ])

      if (snapshot) {
        const newCompleted = snapshot.completed_lessons + 1
        const newPercentage =
          snapshot.total_lessons === 0
            ? 0
            : Math.round((newCompleted / snapshot.total_lessons) * 100)

        queryClient.setQueryData<EnrollmentProgress>(
          ['enrollment-progress', enrollment_id],
          {
            ...snapshot,
            completed_lessons: newCompleted,
            progress_percentage: newPercentage,
          }
        )
      }

      return { snapshot, enrollment_id }
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshot) {
        queryClient.setQueryData(
          ['enrollment-progress', context.enrollment_id],
          context.snapshot
        )
      }
      toast.error('Failed to mark lesson complete')
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['enrollment-progress', variables.enrollment_id],
      })
      queryClient.invalidateQueries({
        queryKey: ['lesson-progress', variables.student_id],
      })
    },
  })
}

// ---- Fetch all enrollments for a student ----
export function useMyEnrollments(studentId: string) {
  return useQuery({
    queryKey: ['enrollments', studentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('enrollments')
        .select('*, trainings ( title )')
        .eq('student_id', studentId)
        .eq('status', 'active')

      if (error) throw error
      return data as (Enrollment & { trainings: { title: string } | null })[]
    },
    enabled: !!studentId,
  })
}

/** Enrollments across all trainings owned by the instructor (PRD Phase 2 Task 10). */
export function useInstructorEnrollments(instructorId: string) {
  return useQuery({
    queryKey: ['enrollments', 'instructor', instructorId],
    queryFn: async () => {
      const { data: trainingRows, error: te } = await supabase
        .from('trainings')
        .select('id')
        .eq('instructor_id', instructorId)

      if (te) throw te
      const trainingIds = trainingRows?.map((t) => t.id) ?? []
      if (trainingIds.length === 0) return [] as InstructorEnrollmentRow[]

      const { data, error } = await supabase
        .from('enrollments')
        .select(
          `
          *,
          trainings ( id, title ),
          profiles ( first_name, last_name )
        `
        )
        .in('training_id', trainingIds)
        .neq('status', 'dropped')

      if (error) throw error
      return data as InstructorEnrollmentRow[]
    },
    enabled: !!instructorId,
  })
}

// ---- Fetch enrollment for a specific training + student ----
export function useStudentEnrollment(trainingId: string, studentId: string) {
  return useQuery({
    queryKey: ['enrollment', trainingId, studentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('enrollments')
        .select('*')
        .eq('training_id', trainingId)
        .eq('student_id', studentId)
        .maybeSingle()

      if (error) throw error
      return data as Enrollment | null
    },
    enabled: !!trainingId && !!studentId,
  })
}

// ---- Fetch all lesson progress for a student in a training ----
export function useStudentLessonProgress(studentId: string, trainingId: string) {
  return useQuery({
    queryKey: ['lesson-progress', studentId, trainingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lesson_progress')
        .select('*')
        .eq('student_id', studentId)

      if (error) throw error
      return data as LessonProgress[]
    },
    enabled: !!studentId && !!trainingId,
  })
}

// ---- Create a chapter ----
export function useCreateChapter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { training_id: string; title: string; sort_order: number }) => {
      const { data, error } = await supabase
        .from('training_chapters')
        .insert(payload)
        .select()
        .single()

      if (error) throw error
      return data as TrainingChapter
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['training', variables.training_id] })
    },
  })
}

// ---- Update a chapter ----
export function useUpdateChapter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      id: string
      training_id: string
      title?: string
      sort_order?: number
    }) => {
      const { id, training_id, ...updates } = payload
      void training_id
      const { data, error } = await supabase
        .from('training_chapters')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as TrainingChapter
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['training', variables.training_id] })
    },
  })
}

// ---- Delete a chapter ----
export function useDeleteChapter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, training_id }: { id: string; training_id: string }) => {
      const { error } = await supabase.from('training_chapters').delete().eq('id', id)
      if (error) throw error
      return { id, training_id }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['training', variables.training_id] })
    },
  })
}

// ---- Reorder chapters ----
export function useReorderChapters() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      chapters,
      training_id,
    }: {
      chapters: { id: string; sort_order: number }[]
      training_id: string
    }) => {
      const updates = chapters.map(({ id, sort_order }) =>
        supabase.from('training_chapters').update({ sort_order }).eq('id', id)
      )
      await Promise.all(updates)
      return { training_id }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['training', variables.training_id] })
    },
  })
}

// ---- Create a lesson ----
export function useCreateLesson() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      chapter_id: string
      training_id: string
      title: string
      content_type: LessonContentType
      sort_order: number
      description?: string | null
      content_url?: string | null
      content_body?: string | null
      duration_minutes?: number | null
    }) => {
      const { training_id, ...insert } = payload
      void training_id
      const { data, error } = await supabase
        .from('training_lessons')
        .insert(insert)
        .select()
        .single()

      if (error) throw error
      return data as TrainingLesson
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['training', variables.training_id] })
    },
  })
}

// ---- Update a lesson ----
export function useUpdateLesson() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      id: string
      training_id: string
      title?: string
      description?: string | null
      content_type?: LessonContentType
      content_url?: string | null
      content_body?: string | null
      duration_minutes?: number | null
      sort_order?: number
    }) => {
      const { id, training_id, ...updates } = payload
      void training_id
      const { data, error } = await supabase
        .from('training_lessons')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as TrainingLesson
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['training', variables.training_id] })
    },
  })
}

// ---- Delete a lesson ----
export function useDeleteLesson() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, training_id }: { id: string; training_id: string }) => {
      const { error } = await supabase.from('training_lessons').delete().eq('id', id)
      if (error) throw error
      return { id, training_id }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['training', variables.training_id] })
    },
  })
}

// ---- Reorder lessons ----
export function useReorderLessons() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      lessons,
      training_id,
    }: {
      lessons: { id: string; sort_order: number }[]
      training_id: string
    }) => {
      const updates = lessons.map(({ id, sort_order }) =>
        supabase.from('training_lessons').update({ sort_order }).eq('id', id)
      )
      await Promise.all(updates)
      return { training_id }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['training', variables.training_id] })
    },
  })
}
